/**
 * Group Members Tab Component
 * Displays and manages group members with add/remove functionality
 */

import React, { useState } from 'react';
import {
  Box,
  Alert,
  Button,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Paper,
  Toolbar,
} from '@mui/material';
import {
  Sync as SyncIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from '../../utils/formatting/dateUtils';
import { getRoleIcon, getRoleColor } from '../../utils/groups/groupUtils';
import { useGroupMembers, useAddGroupMember, useRemoveGroupMember } from '../../hooks/api/useGroups';
import { UserSelectionDialog } from './UserSelectionDialog';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { log } from '../../utils/errors/logger';

interface Group {
  id: string;
  displayName?: string;
  source: string;
  last_synced?: string;
}

interface GroupMembersTabProps {
  group: Group;
  syncing?: boolean;
  onSyncMembers?: () => void;
  canManageMembers?: boolean;
}

export const GroupMembersTab: React.FC<GroupMembersTabProps> = ({
  group,
  syncing = false,
  onSyncMembers,
  canManageMembers = false,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [userSelectionOpen, setUserSelectionOpen] = useState(false);
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<{ element: HTMLElement; userId: string } | null>(null);

  // Debug state changes
  React.useEffect(() => {
    log.debug('userSelectionOpen changed', { userSelectionOpen });
  }, [userSelectionOpen]);
  
  const isLocalGroup = group.source === 'local';
  const isExternalGroup = !isLocalGroup;

  // Fetch members
  const { data: membersResponse, isLoading: membersLoading, refetch } = useGroupMembers(group.id);
  const members = membersResponse?.members || [];

  // Mutations
  const addMemberMutation = useAddGroupMember();
  const removeMemberMutation = useRemoveGroupMember();

  // Extract existing member IDs for exclusion
  const existingMemberIds = members.map(member => member.user_id || member.id);

  const handleAddMembers = () => {
    log.user.action('open_add_members_dialog', 'GroupMembersTab', { groupId: group.id });
    setUserSelectionOpen(true);
  };

  const handleConfirmAddMembers = async (selectedUsers: Array<{ userId: string; role: string }>) => {
    try {
      // Add all selected users
      const promises = selectedUsers.map(({ userId, role }) =>
        addMemberMutation.mutateAsync({ groupId: group.id, userId, role })
      );
      
      await Promise.all(promises);
      
      showSuccess(t('groups.members.addSuccess', { count: selectedUsers.length }));
      setUserSelectionOpen(false);
      refetch();
      log.user.action('add_members_success', 'GroupMembersTab', { 
        groupId: group.id, 
        count: selectedUsers.length 
      });
    } catch (error) {
      showError(t('groups.members.addError'));
      log.error('Failed to add members', { error, groupId: group.id });
    }
  };

  const handleOpenMemberMenu = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setMemberMenuAnchor({ element: event.currentTarget, userId });
  };

  const handleCloseMemberMenu = () => {
    setMemberMenuAnchor(null);
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMemberMutation.mutateAsync({ groupId: group.id, userId });
      showSuccess(t('groups.members.removeSuccess'));
      handleCloseMemberMenu();
      refetch();
      log.user.action('remove_member_success', 'GroupMembersTab', { groupId: group.id, userId });
    } catch (error) {
      showError(t('groups.members.removeError'));
      log.error('Failed to remove member', { error, groupId: group.id, userId });
    }
  };

  const handleEditMemberRole = (userId: string) => {
    // TODO: Implement role editing
    handleCloseMemberMenu();
    log.user.action('edit_member_role', 'GroupMembersTab', { groupId: group.id, userId });
  };

  // Loading state
  if (membersLoading) {
    return (
      <Box>
        <List>
          {[1, 2, 3].map((i) => (
            <ListItem key={i}>
              <ListItemAvatar>
                <Skeleton variant="circular" width={40} height={40} />
              </ListItemAvatar>
              <ListItemText
                primary={<Skeleton variant="text" width="60%" />}
                secondary={<Skeleton variant="text" width="40%" />}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  // Empty state
  if (members.length === 0) {
    return (
      <Box>
        {isExternalGroup && onSyncMembers && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'info.lighter',
              borderLeft: 3,
              borderColor: 'info.main',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2} flex={1}>
                <InfoIcon color="info" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('groups.sync.title', { source: (group.source || 'unknown').toUpperCase() })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.last_synced
                      ? t('groups.sync.lastSynced', { time: formatDistanceToNow(new Date(group.last_synced), { addSuffix: true }) })
                      : t('groups.sync.neverSynced')
                    }
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<SyncIcon />}
                onClick={onSyncMembers}
                disabled={syncing}
              >
                {syncing ? t('groups.details.syncing') : t('groups.sync.syncNow')}
              </Button>
            </Box>
          </Paper>
        )}

        <Box textAlign="center" py={6}>
          <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.primary" fontWeight={600} gutterBottom>
            {t('groups.details.emptyState.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            {t('groups.details.emptyState.description')}
          </Typography>

          {isLocalGroup && canManageMembers && (
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={handleAddMembers}
            >
              {t('groups.members.addFirstMember')}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Members list
  return (
    <Box>
      {/* External Group Sync Toolbar */}
      {isExternalGroup && onSyncMembers && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'info.lighter',
            borderLeft: 3,
            borderColor: 'info.main',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2} flex={1}>
              <InfoIcon color="info" />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('groups.sync.title', { source: (group.source || 'unknown').toUpperCase() })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.last_synced
                    ? t('groups.sync.lastSynced', { time: formatDistanceToNow(new Date(group.last_synced), { addSuffix: true }) })
                    : t('groups.sync.neverSynced')
                  }
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<SyncIcon />}
              onClick={onSyncMembers}
              disabled={syncing}
            >
              {syncing ? t('groups.details.syncing') : t('groups.sync.syncNow')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Add Members Button for Local Groups */}
      {isLocalGroup && canManageMembers && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={handleAddMembers}
            fullWidth
          >
            {t('groups.members.addMembers')}
          </Button>
        </Box>
      )}

      {/* Members List */}
      <List>
        {members.map((member, index) => {
          const RoleIcon = getRoleIcon(member.role);
          const memberId = member.user_id || member.id;

          return (
            <React.Fragment key={memberId}>
              <ListItem
                sx={{
                  '&:hover .member-actions': {
                    opacity: 1,
                  },
                }}
                secondaryAction={
                  isLocalGroup && canManageMembers && (
                    <Box className="member-actions" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveMember(memberId)}
                        color="error"
                        size="small"
                        sx={{ mr: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={(e) => handleOpenMemberMenu(e, memberId)}
                        size="small"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <RoleIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight={500}>
                        {member.full_name || member.username || `User ${memberId.substring(0, 8)}`}
                      </Typography>
                      <Chip
                        size="small"
                        label={member.role}
                        color={getRoleColor(member.role)}
                        variant="filled"
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {member.joined_at && (
                        <>
                          {t('groups.details.joinedTime', {
                            time: formatDistanceToNow(new Date(member.joined_at), { addSuffix: true })
                          })}
                          {member.joined_by && member.joined_by !== 'system' && member.joined_by !== 'azure_sync' &&
                            ` • ${t('groups.details.addedBy', {
                              user: member.joined_by_name || member.joined_by_username || member.joined_by
                            })}`
                          }
                        </>
                      )}
                      {member.email && ` • ${member.email}`}
                    </Typography>
                  }
                />
              </ListItem>
              {index < members.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>

      {/* Member Action Menu */}
      <Menu
        anchorEl={memberMenuAnchor?.element}
        open={Boolean(memberMenuAnchor)}
        onClose={handleCloseMemberMenu}
      >
        {/* Edit Role - Currently disabled */}
        {/* <MenuItem onClick={() => memberMenuAnchor && handleEditMemberRole(memberMenuAnchor.userId)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('groups.members.editRole')}
        </MenuItem> */}
        <MenuItem
          onClick={() => memberMenuAnchor && handleRemoveMember(memberMenuAnchor.userId)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('groups.members.removeMember')}
        </MenuItem>
      </Menu>

      {/* User Selection Dialog */}
      <UserSelectionDialog
        open={userSelectionOpen}
        onClose={() => setUserSelectionOpen(false)}
        onConfirm={handleConfirmAddMembers}
        excludeUserIds={existingMemberIds}
        groupName={group.displayName}
        isLoading={addMemberMutation.isPending}
      />
    </Box>
  );
};